import type { LonLat } from "@atl/contracts";
import { cityContains, MAP_CITY } from "../city.ts";
import { DEFAULT_THEME, isTheme, type Theme } from "./theme.ts";
import type { DepartureDay } from "./time.ts";

/**
 * Shareable page state (docs/tasks/T-40 п.7): a journalist gets a link and sees the same points,
 * time, tab and fleet. Only what changes the request or the view is kept; the responses are not.
 * Live navigation, GPS fixes and rerouted origins belong to the session and never enter this state.
 */
export type Tab = "route" | "dispatch" | "transit";
export type TrafficMode = "traffic" | "free_flow";

export interface Place {
  point: LonLat;
  /** Address label from the geocoder; `null` for a map click. */
  label: string | null;
  /**
   * The short name of a named place (`GeocodeResult.name`, docs/tasks/T-66 п.1): the field shows it,
   * the rest of the label goes under it. Links keep only `fl`/`tl`; parsing restores the name from the
   * label by `PLACE_SEPARATOR`.
   */
  name?: string;
}

/** What the geocoder puts between a name and the rest of a label: "Traveler's Coffee Gagarin · кафе · …". */
export const PLACE_SEPARATOR = " · ";

/** The name before the first separator of a label, if the label has one. */
export function placeName(label: string | null): string | undefined {
  if (label === null) return undefined;
  const index = label.indexOf(PLACE_SEPARATOR);
  return index > 0 ? label.slice(0, index) : undefined;
}

/** A place with its name restored from the label (a link, a stored place). */
export function namedPlace(point: LonLat, label: string | null): Place {
  const name = placeName(label);
  return name === undefined ? { point, label } : { point, label, name };
}

export interface LayerFlags {
  traffic: boolean;
  cameras: boolean;
  lanes: boolean;
  dataQuality: boolean;
  buildings: boolean;
  buildings3d: boolean;
  entrances: boolean;
}

export interface UrlState {
  tab: Tab;
  alt: number;
  from: Place | null;
  to: Place | null;
  /** `null` = "now" (the request omits `departAtMin`). */
  departAtMin: number | null;
  /** Relative calendar choice; the API profiles depend only on the time of day. */
  departDay: DepartureDay;
  traffic: TrafficMode;
  /** Seed of the dispatcher's fleet. */
  seed: number;
  call: LonLat | null;
  layers: LayerFlags;
  /** `auto` follows the sun over Almaty (T-70); only an explicit choice enters the link. */
  theme: Theme;
  /**
   * The "Транспорт" tab (docs/tasks/T-76 п.2): the chosen line (`bus-12`), its direction (the index of
   * its variant) and an open stop card (`n123`). Ids of the transit file; unknown ones are dropped
   * once the file has loaded.
   */
  line: string | null;
  dir: number;
  stop: string | null;
}

/**
 * The lane scheme is on by default (docs/tasks/T-50 п.3): it only appears from zoom 15 on, and a
 * link with `layers=…` still says what is on, as before. These are desktop defaults; without an
 * explicit layers list, `parseUrlState` disables volumes on narrow screens (T-52).
 */
export const DEFAULT_LAYERS: LayerFlags = {
  traffic: false,
  cameras: MAP_CITY.features.cameras,
  lanes: true,
  dataQuality: false,
  buildings: true,
  buildings3d: true,
  entrances: true,
};
export const DEFAULT_SEED = 1;

export const DEFAULT_URL_STATE: UrlState = {
  tab: "route",
  alt: 0,
  from: null,
  to: null,
  departAtMin: null,
  departDay: 0,
  traffic: "traffic",
  seed: DEFAULT_SEED,
  call: null,
  layers: DEFAULT_LAYERS,
  theme: DEFAULT_THEME,
  line: null,
  dir: 0,
  stop: null,
};

const LAYER_KEYS: (keyof LayerFlags)[] = [
  "traffic",
  "lanes",
  "dataQuality",
  "buildings",
  "buildings3d",
  "entrances",
  "cameras",
];

/** "43.23580,76.96004": five decimals, about a metre. */
export function formatPoint(point: LonLat): string {
  return `${point.lat.toFixed(5)},${point.lon.toFixed(5)}`;
}

export function parsePoint(text: string | null): LonLat | null {
  if (text === null) return null;
  const parts = text.split(",");
  if (parts.length !== 2) return null;
  const lat = Number(parts[0]);
  const lon = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  if (MAP_CITY.id !== "almaty" && !cityContains({ lat, lon })) return null;
  return { lat, lon };
}

function parsePlace(params: URLSearchParams, key: string, labelKey: string): Place | null {
  const point = parsePoint(params.get(key));
  if (point === null || (MAP_CITY.id !== "almaty" && !cityContains(point))) return null;
  const label = params.get(labelKey);
  return namedPlace(point, label !== null && label.length > 0 ? label : null);
}

function parseLayers(text: string | null, narrow: boolean): LayerFlags {
  if (text === null) return { ...DEFAULT_LAYERS, buildings3d: !narrow };
  const enabled = new Set(text.split(",").filter((s) => s.length > 0));
  return {
    traffic: enabled.has("traffic"),
    cameras: MAP_CITY.features.cameras && enabled.has("cameras"),
    lanes: enabled.has("lanes"),
    dataQuality: enabled.has("dataQuality"),
    buildings: enabled.has("buildings"),
    buildings3d: enabled.has("buildings3d"),
    entrances: enabled.has("entrances"),
  };
}

/** `theme=auto|day|night`; anything else (including the old links without it) is the default. */
function parseTheme(text: string | null): Theme {
  return isTheme(text) ? text : DEFAULT_THEME;
}

const TABS: readonly Tab[] = ["route", "dispatch", "transit"];
/** Line ids are `<mode>-<slug>` in Latin letters, stop ids an OSM element (`n123`, `w45`). */
const LINE_ID = /^[a-z_]+-[a-z0-9-]+$/;
const STOP_ID = /^[nw]\d+$/;

function parseId(text: string | null, pattern: RegExp): string | null {
  return text !== null && text.length <= 64 && pattern.test(text) ? text : null;
}

/** Parses `location.search`; anything malformed falls back to the default, never throws. */
export function parseUrlState(search: string, narrow = false): UrlState {
  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  const t = Number(params.get("t"));
  const seed = Number(params.get("seed"));
  const alt = Number(params.get("alt"));
  const dir = Number(params.get("dir"));
  const departAtMin =
    params.has("t") && params.get("t")?.trim() !== "" && Number.isInteger(t) && t >= 0 && t <= 1440
      ? t
      : null;
  return {
    tab: TABS.find((t) => t === tab && (t !== "transit" || MAP_CITY.features.transit)) ?? "route",
    alt: Number.isInteger(alt) && alt >= 0 && alt <= 2 ? alt : 0,
    from: parsePlace(params, "from", "fl"),
    to: parsePlace(params, "to", "tl"),
    departAtMin,
    departDay: departAtMin !== null && params.get("td") === "1" ? 1 : 0,
    traffic: params.get("mode") === "free" ? "free_flow" : "traffic",
    seed: Number.isInteger(seed) && seed > 0 ? seed : DEFAULT_SEED,
    call: parsePoint(params.get("call")),
    layers: parseLayers(params.get("layers"), narrow),
    theme: parseTheme(params.get("theme")),
    line: parseId(params.get("line"), LINE_ID),
    dir: Number.isInteger(dir) && dir >= 0 && dir <= 9 ? dir : 0,
    stop: parseId(params.get("stop"), STOP_ID),
  };
}

/** Layers are explicit so sharing/reloading keeps the choice across desktop and phone defaults. */
export function serializeUrlState(state: UrlState): string {
  const params = new URLSearchParams();
  if (state.alt > 0) params.set("alt", String(state.alt));
  if (state.tab !== "route") params.set("tab", state.tab);
  if (state.from !== null) {
    params.set("from", formatPoint(state.from.point));
    if (state.from.label !== null) params.set("fl", state.from.label);
  }
  if (state.to !== null) {
    params.set("to", formatPoint(state.to.point));
    if (state.to.label !== null) params.set("tl", state.to.label);
  }
  if (state.departAtMin !== null) params.set("t", String(state.departAtMin));
  if (state.departAtMin !== null && state.departDay === 1) params.set("td", "1");
  if (state.traffic === "free_flow") params.set("mode", "free");
  if (state.seed !== DEFAULT_SEED) params.set("seed", String(state.seed));
  if (state.call !== null) params.set("call", formatPoint(state.call));
  const enabled = LAYER_KEYS.filter((k) => state.layers[k]);
  params.set("layers", enabled.length > 0 ? enabled.join(",") : "none");
  if (state.theme !== DEFAULT_THEME) params.set("theme", state.theme);
  // The transit choice belongs to its tab: a link of another tab does not carry it.
  if (state.tab === "transit") {
    if (state.line !== null) params.set("line", state.line);
    if (state.line !== null && state.dir > 0) params.set("dir", String(state.dir));
    if (state.stop !== null) params.set("stop", state.stop);
  }
  const text = params.toString();
  return text.length > 0 ? `?${text}` : "";
}
